import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { DataTable, EmptyState, Badge } from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import { productService } from '@/services/product.service';
import type { CurrencyCode } from '@bbpos/types';
import { formatPaise, formatNumber } from '@/utils/currency';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import type { Product } from '@/types/product';
import { classifyStockStatus, stockValuePaise } from './inventory-status';

// ─── Design tokens ──────────────────────────────────────────────────────────
const GLASS_PANEL =
  'rounded-2xl bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm shadow-[0_12px_30px_-18px_rgba(15,23,42,0.22)] dark:shadow-[0_12px_30px_-18px_rgba(0,0,0,0.5)]';

const STATUS_VARIANT = {
  in_stock: 'success' as const,
  low_stock: 'warning' as const,
  out_of_stock: 'danger' as const,
};

const STATUS_LABEL = {
  in_stock: 'In Stock',
  low_stock: 'Low',
  out_of_stock: 'Out',
};

type Row = Product & { id: string };

function makeColumns(currency: CurrencyCode): ColumnDef<Row>[] {
  return [
    {
      key: 'name',
      header: 'Product',
      render: (v, row) => (
        <div>
          <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{v as string}</p>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 tabular-nums">{row.sku}</p>
        </div>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      render: (v) => (
        <span className="text-sm text-neutral-700 dark:text-neutral-300">
          {(v as string) || '—'}
        </span>
      ),
    },
    {
      key: 'stock_quantity',
      header: 'Stock',
      render: (_v, row) => (
        <span className="tabular-nums text-sm text-neutral-900 dark:text-neutral-100">
          {formatNumber(row.stock_quantity)} {row.unit}
        </span>
      ),
    },
    {
      key: 'cost',
      header: 'Cost',
      render: (_v, row) => (
        <span className="tabular-nums text-sm text-neutral-700 dark:text-neutral-300">
          {formatPaise(row.cost, currency)}
        </span>
      ),
    },
    {
      key: 'id',
      header: 'Stock Value',
      render: (_v, row) => (
        <span className="tabular-nums text-sm font-medium text-neutral-900 dark:text-neutral-100">
          {formatPaise(stockValuePaise(row), currency)}
        </span>
      ),
    },
    {
      key: 'low_stock_threshold',
      header: 'Status',
      render: (_v, row) => {
        const status = classifyStockStatus(row);
        return (
          <Badge variant={STATUS_VARIANT[status]} size="sm">
            {STATUS_LABEL[status]}
          </Badge>
        );
      },
    },
  ];
}

interface LocationInventoryTabProps {
  locationId: string;
}

export function LocationInventoryTab({ locationId }: LocationInventoryTabProps) {
  const navigate = useNavigate();
  const currency = useTenantCurrency();
  const columns = useMemo(() => makeColumns(currency), [currency]);

  const { data, isLoading } = useQuery({
    queryKey: ['location', locationId, 'inventory'],
    queryFn: () => productService.list({ location_id: locationId, limit: 100 }),
  });

  const products = data?.data ?? [];

  if (!isLoading && products.length === 0) {
    return (
      <div className="animate-slide-up">
        <EmptyState
          variant="page"
          title="No products at this location"
          description="Add products and assign opening stock to see inventory here."
          action={{ label: 'Manage Products', onClick: () => navigate('/products') }}
          icon={
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-teal-500/20 to-emerald-500/20 text-teal-600 dark:text-teal-400">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-8 w-8" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </div>
          }
        />
      </div>
    );
  }

  return (
    <div className={`${GLASS_PANEL} overflow-hidden animate-slide-up transition-shadow duration-200 hover:shadow-lg`}>
      <DataTable<Row>
        columns={columns}
        data={products}
        loading={isLoading}
        variant="default"
        emptyState={{ title: 'No products', description: 'No products assigned to this location' }}
      />
    </div>
  );
}
